import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { ClientsService, type ClientDTO } from '@/services/ClientsService';
import { AttendeesService } from '@/services/AttendeesService';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { ClientInfoCard } from './ClientInfoCard';
import { EventInfoCard } from './EventInfoCard';
import { RequirementsCard } from './RequirementsCard';

interface ContextSidebarProps {
  eventId: string;
  onClose: () => void;
  onSwitchEventClick: () => void;
}

export function ContextSidebar({ eventId, onClose, onSwitchEventClick }: ContextSidebarProps) {
  const [event, setEvent] = useState<EventDTO | null>(null);
  const [client, setClient] = useState<ClientDTO | null>(null);
  const [attendeeCount, setAttendeeCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [eventId]);

  async function loadData() {
    try {
      setLoading(true);
      const eventsService = new EventsService();
      const clientsService = new ClientsService();
      const attendeesService = new AttendeesService();

      const eventData = await eventsService.get(eventId);
      setEvent(eventData);

      const clientData = await clientsService.get(eventData.client_id);
      setClient(clientData);

      try {
        const attendees = await attendeesService.listByEvent(eventId, 1, 100);
        // Use pagination totalItems instead of items.length to get accurate count
        setAttendeeCount(attendees.pagination?.totalItems || attendees.items.length);
      } catch {
        setAttendeeCount(0);
      }
    } catch (error) {
      console.error('Failed to load context data:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="h-full bg-white border-r flex items-center justify-center p-6">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!event || !client) {
    return (
      <div className="h-full bg-white border-r flex items-center justify-center p-6">
        <p className="text-muted-foreground">Failed to load event data</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-white border-r flex flex-col">
      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Action Buttons - Side by Side */}
        <div className="flex gap-2">
          <button
            onClick={onSwitchEventClick}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            Switch Event
          </button>
          <Link
            to="/organization/venues"
            className="flex-1 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Go to Jungle
          </Link>
        </div>
        
        <ClientInfoCard client={client} />
        <EventInfoCard event={event} attendeeCount={attendeeCount} />
        
        <RequirementsCard
          eventId={event.id}
          initialNotes={event.event_notes || ''}
          onNotesUpdate={(notes) => setEvent({ ...event, event_notes: notes })}
        />
      </div>
    </div>
  );
}

